/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/custom/GooglePlacesAutocomplete.tsx
 * Release: 1.0.0 - Autocomplete Google Places per meccanici
 * Descrizione: Componente per ricerca meccanici con Google Places API
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, MapPin, AlertCircle, CheckCircle } from 'lucide-react';

interface GooglePlace {
  place_id: string;
  name: string;
  formatted_address: string;
  phone?: string;
  email?: string;
  website?: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  types: string[];
}

interface GooglePlacesAutocompleteProps {
  value?: any;
  onChange?: (value: any) => void;
  onFocus?: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  onPlaceSelect?: (place: GooglePlace) => void;
  // Props per la geolocalizzazione
  userAddress?: {
    lat?: number;
    lng?: number;
    address?: string;
  };
}

const MAX_SEARCHES_PER_SESSION = 50;
const SEARCH_COUNT_KEY = 'google_places_search_count';

export const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  value,
  onChange,
  onFocus,
  disabled = false,
  placeholder = 'Cerca meccanico, officina o stazione di servizio...',
  className = '',
  onPlaceSelect,
  userAddress
}) => {
  const [inputValue, setInputValue] = useState('');
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchCount, setSearchCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<GooglePlace | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Inizializza i servizi Google Places
  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      if (inputRef.current) {
        placesService.current = new window.google.maps.places.PlacesService(inputRef.current);
      }
    }
  }, []);

  // Carica il contatore delle ricerche dalla sessione
  useEffect(() => {
    const savedCount = sessionStorage.getItem(SEARCH_COUNT_KEY);
    if (savedCount) {
      setSearchCount(parseInt(savedCount, 10));
    }
  }, []);

  // Salva il contatore delle ricerche nella sessione
  useEffect(() => {
    sessionStorage.setItem(SEARCH_COUNT_KEY, searchCount.toString());
  }, [searchCount]);

  // Inizializza il valore dell'input
  useEffect(() => {
    if (value && typeof value === 'string') {
      setInputValue(value);
    }
  }, [value]);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((input: string) => {
      if (!input.trim() || searchCount >= MAX_SEARCHES_PER_SESSION) {
        setPredictions([]);
        setShowDropdown(false);
        return;
      }

      if (!autocompleteService.current) {
        setError('Servizio temporaneamente non disponibile');
        return;
      }

      setIsLoading(true);
      setError(null);

      const request: google.maps.places.AutocompletionRequest = {
        input,
        types: ['establishment'],
        componentRestrictions: { country: 'it' }, // Limita all'Italia
        // Aggiungi geolocalizzazione se disponibile
        ...(userAddress?.lat && userAddress?.lng && {
          location: new window.google.maps.LatLng(userAddress.lat, userAddress.lng),
          radius: 50000 // 50km di raggio
        })
      };

      autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
        setIsLoading(false);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          // Filtra solo meccanici, officine e stazioni di servizio
          const filteredPredictions = predictions.filter(prediction => {
            const types = prediction.types || [];
            return types.some(type => 
              type === 'car_repair' || 
              type === 'automotive' || 
              type === 'gas_station' ||
              type === 'establishment'
            );
          });
          
          setPredictions(filteredPredictions);
          setShowDropdown(filteredPredictions.length > 0);
          
          // NON incrementare qui il contatore - solo dopo conferma
        } else if (status === window.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
          setError('Servizio temporaneamente non disponibile');
        } else {
          setPredictions([]);
          setShowDropdown(false);
        }
      });
    }, 500),
    [searchCount]
  );

  // Gestisce il cambio di input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue); // Chiama onChange per il sistema di form
    
    if (searchCount >= MAX_SEARCHES_PER_SESSION) {
      setError(`Limite di ${MAX_SEARCHES_PER_SESSION} ricerche raggiunto per questa sessione`);
      return;
    }
    
    debouncedSearch(newValue);
  };

  // Gestisce la selezione di un luogo
  const handlePlaceSelect = (prediction: google.maps.places.AutocompletePrediction) => {
    if (!placesService.current) {
      setError('Servizio temporaneamente non disponibile');
      return;
    }

    setIsLoading(true);
    setError(null);

    const request: google.maps.places.PlaceDetailsRequest = {
      placeId: prediction.place_id,
      fields: ['name', 'formatted_address', 'formatted_phone_number', 'website', 'geometry', 'types']
    };

    placesService.current.getDetails(request, (place, status) => {
      setIsLoading(false);
      
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        const googlePlace: GooglePlace = {
          place_id: place.place_id || '',
          name: place.name || '',
          formatted_address: place.formatted_address || '',
          phone: place.formatted_phone_number || undefined,
          website: place.website || undefined,
          geometry: {
            location: {
              lat: place.geometry?.location?.lat() || 0,
              lng: place.geometry?.location?.lng() || 0
            }
          },
          types: place.types || []
        };
        
        setSelectedPlace(googlePlace);
        setShowConfirmModal(true);
        setShowDropdown(false);
        setInputValue(prediction.description || '');
      } else {
        setError('Errore nel recupero dei dettagli del luogo');
      }
    });
  };

  // Conferma la selezione
  const handleConfirmSelection = () => {
    if (selectedPlace) {
      // Popola i campi del form con i dati del luogo
      onPlaceSelect?.(selectedPlace);
      
      // Aggiorna il valore del campo di ricerca
      const searchValue = selectedPlace.name;
      setInputValue(searchValue);
      onChange?.(searchValue);
      
      // Popola automaticamente i campi del form
      populateFormFields(selectedPlace);
      
      // Incrementa il contatore delle ricerche SOLO dopo la conferma
      setSearchCount(prev => prev + 1);
      
      setSelectedPlace(null);
      setShowConfirmModal(false);
    }
  };

  // Funzione per popolare automaticamente i campi del form
  const populateFormFields = (place: GooglePlace) => {
    // Crea un evento custom per comunicare con il form padre
    const populateEvent = new CustomEvent('populateMechanicFields', {
      detail: {
        ragioneSociale: place.name,
        indirizzo: place.formatted_address,
        telefono: place.phone || '',
        website: place.website || '',
        coordinate: place.geometry.location
      }
    });
    
    // Invia l'evento al documento
    document.dispatchEvent(populateEvent);
  };

  // Annulla la selezione
  const handleCancelSelection = () => {
    setSelectedPlace(null);
    setShowConfirmModal(false);
    setInputValue('');
  };

  // Gestisce il click fuori dal dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const remainingSearches = MAX_SEARCHES_PER_SESSION - searchCount;
  const isLimitReached = searchCount >= MAX_SEARCHES_PER_SESSION;

  return (
    <div className={`relative ${className}`}>
      {/* Campo di input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={onFocus}
          placeholder={placeholder}
          disabled={disabled || isLimitReached}
          className={`w-full px-4 py-3 pl-12 bg-slate-700/50 border rounded-lg text-white placeholder-slate-400 transition-colors focus:outline-none focus:ring-2 ${
            disabled || isLimitReached
              ? 'border-slate-600 opacity-60 cursor-not-allowed'
              : error
              ? 'border-red-500 focus:ring-red-500/20'
              : 'border-slate-600 focus:border-teal-500 focus:ring-teal-500/20'
          }`}
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
        
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-teal-500"></div>
          </div>
        )}
      </div>

      {/* Contatore ricerche rimanenti */}
      {remainingSearches <= 2 && remainingSearches > 0 && (
        <div className="flex items-center gap-1 mt-1 text-amber-400 text-xs">
          <AlertCircle size={12} />
          <span>Ricerche rimanenti: {remainingSearches}</span>
        </div>
      )}

      {/* Messaggio limite raggiunto */}
      {isLimitReached && (
        <div className="flex items-center gap-1 mt-1 text-red-400 text-xs">
          <AlertCircle size={12} />
          <span>Limite di ricerche raggiunto. Inserimento manuale disponibile.</span>
        </div>
      )}

      {/* Messaggio errore */}
      {error && (
        <div className="flex items-center gap-1 mt-1 text-red-400 text-xs">
          <AlertCircle size={12} />
          <span>{error}</span>
        </div>
      )}

      {/* Dropdown risultati */}
      {showDropdown && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {predictions.map((prediction) => (
            <button
              key={prediction.place_id}
              onClick={() => handlePlaceSelect(prediction)}
              className="w-full px-4 py-3 text-left text-white hover:bg-slate-700 transition-colors border-b border-slate-600 last:border-b-0"
            >
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{prediction.structured_formatting?.main_text}</div>
                  <div className="text-xs text-slate-400 truncate">{prediction.structured_formatting?.secondary_text}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Modal di conferma */}
      {showConfirmModal && selectedPlace && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-teal-500" />
              <h3 className="text-lg font-medium text-white">Conferma dati meccanico</h3>
            </div>
            
            <div className="space-y-3 mb-6">
              <div>
                <label className="text-sm font-medium text-slate-300">Ragione Sociale</label>
                <p className="text-white">{selectedPlace.name}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-300">Indirizzo</label>
                <p className="text-white">{selectedPlace.formatted_address}</p>
              </div>
              
              {selectedPlace.phone && (
                <div>
                  <label className="text-sm font-medium text-slate-300">Telefono</label>
                  <p className="text-white">{selectedPlace.phone}</p>
                </div>
              )}
              
              {selectedPlace.website && (
                <div>
                  <label className="text-sm font-medium text-slate-300">Website</label>
                  <p className="text-white">{selectedPlace.website}</p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleConfirmSelection}
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Conferma
              </button>
              <button
                onClick={handleCancelSelection}
                className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Utility function per debounce
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
} 